import { ColDef } from "ag-grid-community";
import { IncomeStatementListComponent } from "./incomeStatementList.component";
import { AgGridColumns, AgGridDropdownCellEditor, AgGridFilterParams, AgGridNumberCellEditor } from "src/app/Compliance/AgGrid";
import { AgGridMultiSelectedCellRenderer, AgGridMultiSelectedHeaderRenderer, AgGridMultiSelectRendererParams } from "src/app/Compliance/AgGrid/MultiSelectTracker";
import { AgGridLinkCellRendererComponent, AgGridLinkCellRendererParams } from "src/app/Compliance/AgGrid/CellRenderers/agGridLinkCellRenderer.component";
import { AgGridYesNoFloatingFilterComponent } from "src/app/Compliance/AgGrid/FloatingFilters/agGridYesNoFloatingFilter.component";
import { CellEditorValidationResult } from "src/app/Compliance/AgGrid/CellEditors/validator.interface";
import { AgGridActionCellRendererComponent, AgGridActionCellRendererParams } from "src/app/Compliance/AgGrid/CellRenderers/agGridActionCellRenderer.component";

export const INCOME_STATEMENT_LIST_COLUMNS = (classRef: IncomeStatementListComponent): ColDef[] => [
    {
        colId: 'grid-column-multiselect',
        headerName: '',
        field: 'incomeStatementLineId',
        width: AgGridColumns.selectionColumnWidth,
        suppressSizeToFit: true,
        resizable: false,
        suppressColumnsToolPanel: true,
        pinned: 'left',
        lockPinned: true,
        lockPosition: true,
        editable: false,
        headerComponent: AgGridMultiSelectedHeaderRenderer,
        headerComponentParams: { tracker: classRef.gridTracker } as AgGridMultiSelectRendererParams,
        cellRenderer: AgGridMultiSelectedCellRenderer,
        cellRendererParams: { tracker: classRef.gridTracker } as AgGridMultiSelectRendererParams
    }, {
        colId: `${Core.IncomeStatementLinePropertyEnum.GLAccountName}`,
        headerName: 'G/L Account',
        // toolPanelClass: 'Source Values',
        field: 'glAccountName',
        pinned: 'left',
        lockPinned: true,
        lockVisible: true,
        lockPosition: true,
        suppressMovable: true,
        width: AgGridColumns.textColumnWidth,
        filter: 'agTextColumnFilter',
        filterParams: AgGridFilterParams.textFilterWithBlankOptionsParams,
        floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams
    }, {
        colId: `${Core.IncomeStatementLinePropertyEnum.CompanyName}`,
        headerName: 'Company',
        // toolPanelClass: 'Source Values',
        field: 'companyName',
        width: AgGridColumns.textColumnWidth,
        filter: 'agTextColumnFilter',
        filterParams: AgGridFilterParams.textFilterWithBlankOptionsParams,
        floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
        hide: true
    }, {
        colId: `${Core.IncomeStatementLinePropertyEnum.TopLevelCompanyName}`,
        headerName: 'Top Level Company',
        // toolPanelClass: 'Source Values',
        field: 'topLevelCompanyName',
        width: AgGridColumns.textColumnWidth,
        filter: 'agTextColumnFilter',
        filterParams: AgGridFilterParams.textFilterWithBlankOptionsParams,
        floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
        hide: true
    }, {
        colId: `${Core.IncomeStatementLinePropertyEnum.SiteName}`,
        headerName: 'Site',
        // toolPanelClass: 'Source Values',
        field: 'siteName',
        width: AgGridColumns.textColumnWidth,
        filter: 'agTextColumnFilter',
        filterParams: AgGridFilterParams.textFilterWithBlankOptionsParams,
        floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
        cellRenderer: AgGridLinkCellRendererComponent,
        cellRendererParams: {
            getHelpContentId: (params: AgGridLinkCellRendererParams) => 'app.view-site',
            newWindow: true,
            getLink: (params: AgGridLinkCellRendererParams) => {
                const lineItem = params.data as Core.IncomeStatementLineModel;
                // if (! lineItem && lineItem.sourceSite)) {
                if (!lineItem) {
                    return '';
                }
                return `#/site/${lineItem.siteId}`;
            }
        } as AgGridLinkCellRendererParams
    }, {
        colId: `${Core.IncomeStatementLinePropertyEnum.SiteNumber}`,
        headerName: 'Site Number',
        field: 'siteNumber',
        width: AgGridColumns.textColumnWidth,
        filter: 'agTextColumnFilter',
        filterParams: AgGridFilterParams.textFilterWithBlankOptionsParams,
        floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
        cellRenderer: AgGridLinkCellRendererComponent,
        cellRendererParams: {
            getHelpContentId: (params: AgGridLinkCellRendererParams) => 'app.view-site',
            newWindow: true,
            getLink: (params: AgGridLinkCellRendererParams) => {
                const lineItem = params.data as Core.IncomeStatementLineModel;
                // if (! lineItem && lineItem.sourceSite)) {
                if (!lineItem) {
                    return '';
                }
                return `#/site/${lineItem.siteId}`;
            }
        } as AgGridLinkCellRendererParams
    }, {
        colId: `${Core.IncomeStatementLinePropertyEnum.IsValidated}`,
        headerName: 'Validated',
        field: 'isValidated',
        type: 'textColumn',
        width: AgGridColumns.textColumnSmallWidth,
        filter: 'agYesNoColumnFilter',
        filterParams: AgGridFilterParams.yesNoFilterParams,
        floatingFilterComponent: AgGridYesNoFloatingFilterComponent,
        floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
        valueFormatter: (params) => {
            const data = params.data as Core.IncomeStatementLineModel;
            return data && data.incomeStatementLineId
                ? data.isValidated === true ? 'Yes' : 'No'
                : '-';
        },
        editable: params => {
            const data = params.data as Core.IncomeStatementLineModel;
            return !!data.incomeStatementLineId;
        },
        cellEditor: AgGridDropdownCellEditor,
        cellEditorParams: {
            getOptions: () => {
                return [
                    { name: 'Yes', value: true },
                    { name: 'No', value: false }
                ];
            },
            cellFocusLost: (params, updatedValue) => classRef.updateIsValidated(params, updatedValue)
        }
    }, {
        colId: `${Core.IncomeStatementLinePropertyEnum.GLAccountNumber}`,
        headerName: 'G/L Account Number',
        // toolPanelClass: 'Source Values',
        field: 'glAccountNumber',
        width: AgGridColumns.textColumnWidth,
        filter: 'agTextColumnFilter',
        filterParams: AgGridFilterParams.textFilterWithBlankOptionsParams,
        floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams
    }, {
        colId: `${Core.IncomeStatementLinePropertyEnum.GLAccountDescription}`,
        headerName: 'G/L Account Description',
        // toolPanelClass: 'Source Values',
        field: 'glAccountDescription',
        width: AgGridColumns.textColumnWidth,
        filter: 'agTextColumnFilter',
        filterParams: AgGridFilterParams.textFilterWithBlankOptionsParams,
        floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
        hide: true
    }, {
        colId: `${Core.IncomeStatementLinePropertyEnum.GLAccountIncomeStatementCategoryName}`,
        headerName: 'Category',
        // toolPanelClass: 'Asset',
        field: 'glAccountIncomeStatementCategoryName',
        width: AgGridColumns.textColumnWidth,
        filter: 'agTextColumnFilter',
        filterParams: AgGridFilterParams.textFilterWithBlankOptionsParams,
        floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams
    }, {
        colId: `${Core.IncomeStatementLinePropertyEnum.GLAccountIncomeStatementCategoryTypeName}`,
        headerName: 'Account Type',
        // toolPanelClass: 'Asset',
        field: 'glAccountIncomeStatementCategoryTypeName',
        width: AgGridColumns.textColumnWidth,
        filter: 'agTextColumnFilter',
        filterParams: AgGridFilterParams.textFilterWithBlankOptionsParams,
        floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams
    }, {
        colId: `${Core.IncomeStatementLinePropertyEnum.CalculatedBalance}`,
        headerName: 'Balance',
        // toolPanelClass: 'Source Values',
        field: 'calculatedBalance',
        type: 'numericColumn',
        width: AgGridColumns.numericColumnWidth,
        filter: 'agNumberColumnFilter',
        filterParams: AgGridFilterParams.numberWithRangeAndEqualToAndBlankFilterParams,
        floatingFilterComponentParams: AgGridFilterParams.numberFloatingFilterParams,
        valueFormatter: (params) => classRef.decimalPipe.transform(params.value, '1.2-2'),
        cellClass: 'ag-numeric-cell',
        cellClassRules: {
            'overridden': params => {
                const data = params.data as Core.IncomeStatementLineModel;
                return data && data.incomeStatementLineId && data.calculatedBalance != null && data.calculatedBalance !== data.importedBalance;
            },
            'inline-editor-disabled': params => {
                const data = params.data as Core.IncomeStatementLineModel;
                return data && data.incomeStatementLineId && data.isValidated;
            }
        },
        editable: params => {
            const data = params.data as Core.IncomeStatementLineModel;
            return data && data.incomeStatementLineId && !data.isValidated;
        },
        cellEditor: AgGridNumberCellEditor,
        cellEditorParams: {
            allowNull: true,
            cellFocusLost: (params, updatedValue) => classRef.updateBalance(params, updatedValue),
            validator: (value: number): CellEditorValidationResult => {
                return value > 9999999999999992
                    ? { isValid: false, validationMessage: 'Value is too big' }
                    : { isValid: true, validationMessage: '' };
            }
        },
        tooltipValueGetter: params => {
            const data = params.data as Core.IncomeStatementLineModel;
            return data && data.incomeStatementLineId && data.isValidated
                ? 'May not edit a validated line item.'
                : '';
        }
    }, {
        colId: `${Core.IncomeStatementLinePropertyEnum.AccountHierarchyDescription}`,
        headerName: 'Account Hierarchy Description',
        // toolPanelClass: 'Source Values',
        field: 'accountHierarchyDescription',
        width: AgGridColumns.textColumnWidth,
        filter: 'agTextColumnFilter',
        filterParams: AgGridFilterParams.textFilterWithBlankOptionsParams,
        floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
        hide: true
    }, {
        colId: `${Core.IncomeStatementLinePropertyEnum.CreateDate}`,
        headerName: 'Import Date',
        field: 'createDate',
        type: 'dateColumn',
        width: AgGridColumns.dateColumnWidth,
        filter: 'agDateColumnFilter',
        filterParams: AgGridFilterParams.dateFilterParams,
        floatingFilterComponentParams: AgGridFilterParams.dateFloatingFilterParams,
        valueFormatter: (params) => classRef.datePipe.transform(params.value, true),
        cellClass: 'ag-date-cell',
        hide: true
    }, {
        colId: `${Core.IncomeStatementLinePropertyEnum.ValidatedBy}`,
        headerName: 'Validated By',
        field: 'validatedBy',
        width: AgGridColumns.textColumnWidth,
        filter: 'agTextColumnFilter',
        filterParams: AgGridFilterParams.textFilterWithBlankOptionsParams,
        floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
        hide: true
    }, {
        colId: `${Core.IncomeStatementLinePropertyEnum.ValidatedDate}`,
        headerName: 'Validated Date',
        field: 'validatedDate',
        type: 'dateColumn',
        width: AgGridColumns.dateColumnWidth,
        filter: 'agDateColumnFilter',
        filterParams: AgGridFilterParams.dateFilterParamsWithBlankOptionsParams,
        floatingFilterComponentParams: AgGridFilterParams.dateFloatingFilterParams,
        valueFormatter: (params) => classRef.datePipe.transform(params.value, true),
        cellClass: 'ag-date-cell',
        hide: true
    }, {
        colId: 'grid-column-actions',
        headerName: '',
        field: 'actions',
        width: AgGridColumns.getActionColumnWidth(1),
        minWidth: AgGridColumns.getActionColumnWidth(1),
        maxWidth: AgGridColumns.getActionColumnWidth(1),
        suppressSizeToFit: true,
        suppressAutoSize: true,
        resizable: false,
        suppressColumnsToolPanel: true,
        lockPinned: true,
        pinned: 'right',
        lockVisible: true,
        sortable: false,
        cellRenderer: AgGridActionCellRendererComponent,
        cellRendererParams: {
            buttonConfigs: [
                {
                    iconClass: 'fa-trash',
                    onClick: classRef.deleteItem.bind(classRef),
                    isShown: (params: AgGridActionCellRendererParams) => classRef.canEdit,
                    buttonClass: 'btn-primary btn-danger',
                    helpContentId: 'app.delete'
                }
            ]
        } as AgGridActionCellRendererParams
    }
]